import { Col, Form, Input, Row, Typography } from "antd"

import { SubmitButton } from "@src/shared/componemts/SubmitButton"
import { useCatchFormErrors } from "@src/shared/form/base_error"

export type SubMerchChangePasswordValues = {
  confirm_submerchant_password: string
  current_merchant_password: string
  new_submerchant_password: string
}

type Props = {
  isLoading?: boolean
  onChangePassword: (values: SubMerchChangePasswordValues) => Promise<unknown>
}

export const SubMerchChangePasswordForm = ({
  isLoading = false,
  onChangePassword,
}: Props) => {
  const [form] = Form.useForm<SubMerchChangePasswordValues>()

  const { submit } = useCatchFormErrors(form, async (values) => {
    const response = await onChangePassword(values)

    if (response) {
      form.resetFields()
    }

    return response
  })

  return (
    <Form
      disabled={isLoading}
      form={form}
      layout="vertical"
      name="subMerchChangePassword"
      onFinish={submit}
      size="large"
      style={{ marginTop: 32 }}
    >
      <Typography.Title style={{ fontSize: 16, opacity: 0.65 }}>
        CHANGE PASSWORD
      </Typography.Title>

      <Row align="top" gutter={16}>
        <Col span={8}>
          <Form.Item
            label="Current merchant password"
            name="current_merchant_password"
            rules={[
              {
                message: "Please input your current password",
                required: true,
              },
            ]}
          >
            <Input.Password placeholder="Enter current password" />
          </Form.Item>
        </Col>

        <Col span={8}>
          <Form.Item
            dependencies={["current_merchant_password"]}
            label="New submerchant password"
            name="new_submerchant_password"
            rules={[
              { message: "Please input a new password", required: true },
              {
                message: "The length must be no less than 8 characters",
                min: 8,
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (
                    !value ||
                    getFieldValue("current_merchant_password") !== value
                  ) {
                    return Promise.resolve()
                  }

                  return Promise.reject(
                    new Error(
                      "The new password must differ from the current password",
                    ),
                  )
                },
              }),
            ]}
          >
            <Input.Password placeholder="Enter new password" />
          </Form.Item>
        </Col>

        <Col span={8}>
          <Form.Item
            dependencies={["new_submerchant_password"]}
            label="Confirm submerchant password"
            name="confirm_submerchant_password"
            rules={[
              { message: "Please confirm the new password", required: true },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (
                    !value ||
                    getFieldValue("new_submerchant_password") === value
                  ) {
                    return Promise.resolve()
                  }

                  return Promise.reject(new Error("Passwords do not match"))
                },
              }),
            ]}
          >
            <Input.Password placeholder="Repeat new password" />
          </Form.Item>
        </Col>
      </Row>

      <SubmitButton
        buttonProps={{
          disabled: isLoading,
          loading: isLoading,
          type: "primary",
        }}
        form={form}
      >
        Change password
      </SubmitButton>
    </Form>
  )
}
